import { Check, ChevronDown, Minus, Plus } from "lucide-react";
import * as React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { waitWords } from "@/lib/cadence-plan";
import { cn } from "@/lib/utils";

/**
 * The cadence builder's editable words. A value in the plan reads as part of its sentence —
 * "again **4 hours** after the last call" — and a click on it opens a small picker with the
 * choices people actually make, a way to type another, and nothing else. No input box sits on
 * the page until someone asks for one, which is what keeps the plan readable at a glance.
 */

export function Token({
  label, children, text, readOnly = false, invalid, title, hint, width = "w-72", content, tone = "accent",
}: {
  /** What the control changes, for a screen reader: "Wait before call 2". */
  label: string;
  /** The value as it reads in the sentence. */
  children: React.ReactNode;
  /** The value in words, when `children` is not a plain string. */
  text?: string;
  readOnly?: boolean;
  /** The server's words for why the value is refused, shown on hover and marked in red. */
  invalid?: string;
  /** The picker's heading. */
  title?: string;
  hint?: React.ReactNode;
  width?: string;
  content: (close: () => void) => React.ReactNode;
  tone?: "accent" | "muted";
}) {
  const [open, setOpen] = React.useState(false);
  if (readOnly) {
    return <span className={cn("font-medium", invalid ? "text-danger-11" : "text-text")}>{children}</span>;
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${label}: ${text ?? (typeof children === "string" ? children : "")}`.trim()}
          title={invalid}
          className={cn(
            "inline-flex max-w-full items-center gap-0.5 rounded-md border px-1.5 py-px align-baseline font-medium leading-5",
            "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
            invalid
              ? "border-danger-9 bg-danger-2 text-danger-11"
              : tone === "muted"
                ? "border-border bg-surface-sunken text-text hover:border-border-strong hover:bg-surface-hover data-[state=open]:border-accent-8"
                : "border-accent-4 bg-accent-2 text-accent-11 hover:border-accent-7 hover:bg-accent-3 data-[state=open]:border-accent-9 data-[state=open]:bg-accent-9 data-[state=open]:text-white dark:data-[state=open]:text-gray-1",
          )}
        >
          <span className="truncate">{children}</span>
          <ChevronDown className="size-3 shrink-0 opacity-60" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn(width, "p-3")} onOpenAutoFocus={(e) => e.preventDefault()}>
        {(title || hint) && (
          <div className="mb-2.5 space-y-0.5">
            {title && <p className="text-xs font-semibold text-text">{title}</p>}
            {hint && <p className="text-xs text-text-muted">{hint}</p>}
          </div>
        )}
        {invalid && <p className="mb-2 rounded-md bg-danger-2 px-2 py-1.5 text-xs text-danger-11">{invalid}</p>}
        {content(() => setOpen(false))}
      </PopoverContent>
    </Popover>
  );
}

// --- the pieces pickers are made of -----------------------------------------------------------

function Choice({
  selected, onClick, children, className,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "rounded-md border px-2 py-1.5 text-xs font-medium tabular-nums transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
        selected
          ? "border-accent-9 bg-accent-9 text-white dark:text-gray-1"
          : "border-border bg-surface text-text hover:border-accent-7 hover:bg-accent-2",
        className,
      )}
    >
      {children}
    </button>
  );
}

function OtherRow({
  label, children, onSubmit, error,
}: {
  label: string;
  children: React.ReactNode;
  onSubmit: () => void;
  error?: string | null;
}) {
  return (
    <form
      className="mt-3 space-y-1 border-t border-border pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSubmit();
      }}
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-text-subtle">{label}</p>
      <div className="flex items-center gap-1.5">
        {children}
        <button
          type="submit"
          className="h-7 rounded-md bg-accent-9 px-2.5 text-xs font-medium text-white hover:bg-accent-10 dark:text-gray-1"
        >
          Set
        </button>
      </div>
      {error && <p className="text-xs text-danger-11">{error}</p>}
    </form>
  );
}

const small = cn(
  "h-7 w-16 rounded-md border border-border bg-surface px-2 text-center text-xs tabular-nums text-text",
  "focus-visible:border-accent-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/25",
);

// --- a wait ---------------------------------------------------------------------------------------

const UNITS = [
  { key: "min", label: "min", minutes: 1 },
  { key: "h", label: "hours", minutes: 60 },
  { key: "d", label: "days", minutes: 24 * 60 },
] as const;

function unitFor(minutes: number) {
  if (minutes > 0 && minutes % (24 * 60) === 0) return UNITS[2];
  if (minutes > 0 && minutes % 60 === 0) return UNITS[1];
  return UNITS[0];
}

/** A wait in minutes: the presets as buttons, and any other in minutes, hours or days. */
export function WaitPicker({
  value, presets, min, max, onChange, close,
}: {
  value: number;
  presets: readonly number[];
  min: number;
  max: number;
  onChange: (minutes: number) => void;
  close: () => void;
}) {
  const [unit, setUnit] = React.useState<(typeof UNITS)[number]>(() => unitFor(value));
  const [amount, setAmount] = React.useState(() => String(Number.isFinite(value) ? value / unitFor(value).minutes : ""));
  const [error, setError] = React.useState<string | null>(null);
  const offered = presets.filter((m) => m >= min && m <= max);
  return (
    <div>
      <div className="grid grid-cols-3 gap-1.5">
        {offered.map((m) => (
          <Choice
            key={m}
            selected={m === value}
            onClick={() => {
              onChange(m);
              close();
            }}
          >
            {waitWords(m)}
          </Choice>
        ))}
      </div>
      <OtherRow
        label="Another wait"
        error={error}
        onSubmit={() => {
          const n = Number(amount);
          const minutes = Math.round(n * unit.minutes);
          if (!amount.trim() || !Number.isFinite(n) || minutes < min || minutes > max) {
            setError(`From ${waitWords(min)} to ${waitWords(max)}`);
            return;
          }
          onChange(minutes);
          close();
        }}
      >
        <input
          aria-label="How long"
          inputMode="decimal"
          className={small}
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            setError(null);
          }}
        />
        <div className="flex rounded-md border border-border p-0.5">
          {UNITS.map((u) => (
            <button
              key={u.key}
              type="button"
              aria-pressed={u.key === unit.key}
              onClick={() => setUnit(u)}
              className={cn(
                "rounded px-1.5 py-0.5 text-[11px] font-medium",
                u.key === unit.key ? "bg-accent-9 text-white dark:text-gray-1" : "text-text-muted hover:text-text",
              )}
            >
              {u.label}
            </button>
          ))}
        </div>
      </OtherRow>
    </div>
  );
}

// --- a day ----------------------------------------------------------------------------------------

/** A day after the lead joined, or "" for no end when the place allows one. */
export function DayPicker({
  value, presets, min, max, openLabel, onChange, close,
}: {
  value: string;
  presets: readonly number[];
  min: number;
  max: number;
  /** Offered when the day may be left open: "No end day". */
  openLabel?: string;
  onChange: (value: string) => void;
  close: () => void;
}) {
  const [amount, setAmount] = React.useState(value);
  const [error, setError] = React.useState<string | null>(null);
  const offered = presets.filter((d) => d >= min && d <= max);
  return (
    <div>
      <div className="grid grid-cols-4 gap-1.5">
        {offered.map((d) => (
          <Choice
            key={d}
            selected={String(d) === value.trim()}
            onClick={() => {
              onChange(String(d));
              close();
            }}
          >
            {d}
          </Choice>
        ))}
      </div>
      {openLabel && (
        <Choice
          className="mt-1.5 w-full"
          selected={value.trim() === ""}
          onClick={() => {
            onChange("");
            close();
          }}
        >
          {openLabel}
        </Choice>
      )}
      <OtherRow
        label="Another day"
        error={error}
        onSubmit={() => {
          const n = Number(amount);
          if (!Number.isInteger(n) || n < min || n > max) {
            setError(`A whole day from ${min} to ${max}`);
            return;
          }
          onChange(String(n));
          close();
        }}
      >
        <span className="text-xs text-text-muted">Day</span>
        <input
          aria-label="Day"
          inputMode="numeric"
          className={small}
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            setError(null);
          }}
        />
      </OtherRow>
    </div>
  );
}

// --- a count --------------------------------------------------------------------------------------

/** A whole number with a stepper that changes it as it is clicked, the common values as buttons,
 *  and, where a cap may be lifted, "No cap". */
export function CountPicker({
  value, min, max, presets = [], noneLabel, onChange, close, unit,
}: {
  value: number | null;
  min: number;
  max: number;
  presets?: readonly number[];
  noneLabel?: string;
  onChange: (value: number | null) => void;
  close: () => void;
  /** "calls", "times": said after the stepper's number. */
  unit?: string;
}) {
  const current = value ?? min;
  const step = (delta: number) => onChange(Math.min(max, Math.max(min, current + delta)));
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Fewer"
          disabled={value !== null && current <= min}
          onClick={() => step(-1)}
          className="grid size-8 place-items-center rounded-md border border-border text-text hover:bg-surface-hover disabled:opacity-40"
        >
          <Minus className="size-3.5" />
        </button>
        <span className="min-w-12 text-center text-lg font-semibold tabular-nums text-text">
          {value === null ? "—" : value}
        </span>
        <button
          type="button"
          aria-label="More"
          disabled={value !== null && current >= max}
          onClick={() => (value === null ? onChange(min) : step(1))}
          className="grid size-8 place-items-center rounded-md border border-border text-text hover:bg-surface-hover disabled:opacity-40"
        >
          <Plus className="size-3.5" />
        </button>
        {unit && <span className="text-xs text-text-muted">{value === null ? "" : unit}</span>}
      </div>
      {(presets.length > 0 || noneLabel) && (
        <div className="flex flex-wrap gap-1.5">
          {presets
            .filter((n) => n >= min && n <= max)
            .map((n) => (
              <Choice
                key={n}
                selected={n === value}
                onClick={() => {
                  onChange(n);
                  close();
                }}
              >
                {n}
              </Choice>
            ))}
          {noneLabel && (
            <Choice
              selected={value === null}
              onClick={() => {
                onChange(null);
                close();
              }}
            >
              {noneLabel}
            </Choice>
          )}
        </div>
      )}
      <p className="text-[11px] text-text-subtle">From {min} to {max}.</p>
    </div>
  );
}

// --- one of a few -------------------------------------------------------------------------------

export interface Option<T extends string> {
  value: T;
  label: string;
  description?: string;
  disabled?: boolean;
}

export function OptionPicker<T extends string>({
  value, options, onChange, close,
}: {
  value: T;
  options: readonly Option<T>[];
  onChange: (value: T) => void;
  close: () => void;
}) {
  return (
    <div className="-mx-1 space-y-0.5" role="listbox">
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="option"
            aria-selected={selected}
            disabled={o.disabled}
            onClick={() => {
              onChange(o.value);
              close();
            }}
            className={cn(
              "flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors",
              "hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
              selected && "bg-accent-2",
            )}
          >
            <Check className={cn("mt-0.5 size-3.5 shrink-0 text-accent-11", !selected && "invisible")} aria-hidden />
            <span className="min-w-0">
              <span className="block text-xs font-medium text-text">{o.label}</span>
              {o.description && <span className="block text-xs text-text-muted">{o.description}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// --- a name -------------------------------------------------------------------------------------

export function TextPicker({
  value, validate, onChange, close, label, mono = false,
}: {
  value: string;
  validate: (value: string) => string | null;
  onChange: (value: string) => void;
  close: () => void;
  label: string;
  mono?: boolean;
}) {
  const [text, setText] = React.useState(value);
  const [error, setError] = React.useState<string | null>(null);
  return (
    <form
      className="space-y-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const problem = validate(text);
        if (problem) {
          setError(problem);
          return;
        }
        onChange(text.trim());
        close();
      }}
    >
      <div className="flex items-center gap-1.5">
        <input
          aria-label={label}
          autoFocus
          onFocus={(e) => e.currentTarget.select()}
          className={cn(small, "w-full text-left", mono && "font-mono")}
          value={text}
          onChange={(e) => {
            setText(mono ? e.target.value.toLowerCase().replace(/\s+/g, "_") : e.target.value);
            setError(null);
          }}
        />
        <button type="submit" className="h-7 rounded-md bg-accent-9 px-2.5 text-xs font-medium text-white hover:bg-accent-10 dark:text-gray-1">
          Set
        </button>
      </div>
      {error && <p className="text-xs text-danger-11">{error}</p>}
    </form>
  );
}

// --- a priority -----------------------------------------------------------------------------------

export interface Rung {
  label: string;
  value: number;
  /** This phase, the one being set. */
  self?: boolean;
}

/**
 * A phase's priority, set against the others it is ranked with: the ladder shows the fixed bands
 * (a promised callback, a lead near signing, the second call of a double dial, a lead never
 * reached) and the other phases, with this one moving as the slider does.
 */
export function PriorityPicker({
  band, decay, others, onChange, selfLabel = "This phase",
}: {
  band: number;
  /** Absent for a fixed band, which does not age. */
  decay?: number;
  others: readonly Rung[];
  onChange: (band: number, decay: number) => void;
  selfLabel?: string;
}) {
  const ladder = [...others, { label: selfLabel, value: band, self: true }].sort((a, b) => b.value - a.value);
  const floor = Math.max(0, band - 100);
  return (
    <div className="space-y-3">
      <ol className="space-y-0.5 rounded-md border border-border bg-surface-sunken p-1.5">
        {ladder.map((r) => (
          <li
            key={`${r.label}-${r.value}`}
            className={cn(
              "flex items-center justify-between gap-2 rounded px-1.5 py-0.5 text-xs",
              r.self ? "bg-accent-9 font-medium text-white dark:text-gray-1" : "text-text-muted",
            )}
          >
            <span className="truncate">{r.label}</span>
            <span className="tabular-nums">{r.value}</span>
          </li>
        ))}
      </ol>
      <label className="block space-y-1">
        <span className="flex items-center justify-between text-xs text-text-muted">
          Priority <span className="font-semibold tabular-nums text-text">{band}</span>
        </span>
        <input
          type="range"
          min={0}
          max={999}
          step={10}
          value={band}
          aria-label="Priority"
          onChange={(e) => onChange(Number(e.target.value), decay ?? 0)}
          className="w-full accent-[var(--color-accent-9)]"
        />
      </label>
      {decay !== undefined && (
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-text-muted">Drops a day while unreached</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Drop less"
            disabled={decay <= 0}
            onClick={() => onChange(band, Math.max(0, decay - 5))}
            className="grid size-6 place-items-center rounded border border-border hover:bg-surface-hover disabled:opacity-40"
          >
            <Minus className="size-3" />
          </button>
          <span className="w-8 text-center text-xs font-semibold tabular-nums text-text">{decay}</span>
          <button
            type="button"
            aria-label="Drop more"
            disabled={decay >= 100}
            onClick={() => onChange(band, Math.min(100, decay + 5))}
            className="grid size-6 place-items-center rounded border border-border hover:bg-surface-hover disabled:opacity-40"
          >
            <Plus className="size-3" />
          </button>
        </div>
      </div>
      )}
      {/* A fixed height: a picker that grew as the text changed could flip above its word
          mid-gesture and take the next click with it. */}
      <p className="min-h-[3.25rem] text-[11px] text-text-subtle">
        Higher is called first when reps are short.
        {decay === undefined
          ? ""
          : decay > 0
            ? ` A lead nobody reaches loses ${decay} a day, down to ${floor}, so fresh leads go ahead of stale ones.`
            : " It stays put however long nobody answers."}
      </p>
    </div>
  );
}
